import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Phone, Send, Facebook, Instagram, Youtube, Twitter, Loader2 } from "lucide-react";
import JazzHeader from "../homepage_components/JazzHeader";
import JazzFooter from "../homepage_components/JazzFooter";
import SEO from "../components/SEO";
import { useNotification } from "../context/NotificationContext";
import { ContactService } from "../services/ContactServices";

const SkeletonLine = ({ className = "" }) => (
    <div className={`animate-pulse bg-black/5 rounded ${className}`} />
);

export default function Contact() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const [submitting, setSubmitting] = useState(false);
    const [settings, setSettings] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        subject: "",
        message: ""
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await ContactService.getSettings();
                if (response.status === "success") {
                    setSettings(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch contact settings:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await ContactService.submitInquiry(formData);
            showNotification({
                type: "success",
                title: "Message Sent",
                message: "Thank you for reaching out. We will get back to you shortly."
            });
            setFormData({ name: "", email: "", subject: "", message: "" });

            // Redirect after a short delay
            setTimeout(() => {
                navigate("/");
            }, 2000);
        } catch (error) {
            showNotification({
                type: "failed",
                title: "Submission Failed",
                message: error.response?.data?.error || "Something went wrong. Please try again later."
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fafafa]">
            <SEO title="Contact & Booking Inquiries" description="Get in touch for concert bookings, collaborations, media inquiries, and management." url="/contact" />
            <JazzHeader />

            <main className="pt-24 sm:pt-32 pb-16 sm:pb-24">
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6">

                    {/* Hero / Title Section */}
                    <div className="mb-16 sm:mb-24 text-center">
                        <h1
                            className="text-4xl sm:text-5xl lg:text-7xl font-normal text-black mb-6"
                            style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                            Get in Touch
                        </h1>
                        <div className="w-24 h-[3px] bg-jazz-brown mx-auto mb-8" />
                        <p
                            className="max-w-2xl mx-auto text-black/90 text-lg leading-relaxed font-serif italic"
                            style={{ fontFamily: "Crimson Pro, Georgia, serif" }}
                        >
                            For booking inquiries, press features, or just to say hello.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">

                        {/* Contact Information */}
                        <div className="space-y-12">
                            <div>
                                <h3 className="text-2xl font-medium text-black mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                                    Management & Booking
                                </h3>
                                <div className="space-y-4">
                                    <div className="flex items-start gap-4">
                                        <Mail className="text-jazz-brown mt-1" size={20} />
                                        <div className="flex-1">
                                            <p className="text-xs font-bold tracking-widest text-black/70 mb-1">GENERAL INQUIRIES</p>
                                            {isLoading ? (
                                                <SkeletonLine className="h-6 w-48" />
                                            ) : (
                                                <Link to={settings?.email ? `mailto:${settings.email}` : "#"} className={`text-lg transition-colors ${settings?.email ? 'text-black hover:text-jazz-brown' : 'text-black/40 italic'}`}>
                                                    {settings?.email || "Inquiry channel opening soon..."}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <Mail className="text-jazz-brown mt-1" size={20} />
                                        <div className="flex-1">
                                            <p className="text-xs font-bold tracking-widest text-black/70 mb-1">BOOKING</p>
                                            {isLoading ? (
                                                <SkeletonLine className="h-6 w-48" />
                                            ) : (
                                                <Link to={settings?.booking_email ? `mailto:${settings.booking_email}` : "#"} className={`text-lg transition-colors ${settings?.booking_email ? 'text-black hover:text-jazz-brown' : 'text-black/40 italic'}`}>
                                                    {settings?.booking_email || "Booking details arriving shortly..."}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <Phone className="text-jazz-brown mt-1" size={20} />
                                        <div className="flex-1">
                                            <p className="text-xs font-bold tracking-widest text-black/70 mb-1">PHONE</p>
                                            {isLoading ? (
                                                <SkeletonLine className="h-6 w-32" />
                                            ) : (
                                                <p className={`text-lg ${settings?.phone ? 'text-black' : 'text-black/40 italic'}`}>
                                                    {settings?.phone || "Voice updates coming your way..."}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {(isLoading || (settings && (settings.facebook || settings.instagram || settings.youtube || settings.twitter))) && (
                                <div>
                                    <h3 className="text-2xl font-medium text-black mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                                        Connect
                                    </h3>
                                    <div className="flex gap-4">
                                        {isLoading ? (
                                            <>
                                                <SkeletonLine className="w-12 h-12" />
                                                <SkeletonLine className="w-12 h-12" />
                                                <SkeletonLine className="w-12 h-12" />
                                            </>
                                        ) : (
                                            <>
                                                {settings?.facebook && (
                                                    <Link
                                                        to={settings.facebook} target="_blank" rel="noreferrer"
                                                        className="w-12 h-12 border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300"
                                                    >
                                                        <Facebook size={20} />
                                                    </Link>
                                                )}
                                                {settings?.instagram && (
                                                    <Link
                                                        to={settings.instagram} target="_blank" rel="noreferrer"
                                                        className="w-12 h-12 border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300"
                                                    >
                                                        <Instagram size={20} />
                                                    </Link>
                                                )}
                                                {settings?.twitter && (
                                                    <Link
                                                        to={settings.twitter} target="_blank" rel="noreferrer"
                                                        className="w-12 h-12 border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300"
                                                    >
                                                        <Twitter size={20} />
                                                    </Link>
                                                )}
                                                {settings?.youtube && (
                                                    <Link
                                                        to={settings.youtube} target="_blank" rel="noreferrer"
                                                        className="w-12 h-12 border border-black/10 flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300"
                                                    >
                                                        <Youtube size={20} />
                                                    </Link>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="p-8 bg-white border-l-4 border-jazz-brown shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
                                {isLoading ? (
                                    <div className="space-y-2">
                                        <SkeletonLine className="h-4 w-full" />
                                        <SkeletonLine className="h-4 w-5/6" />
                                        <SkeletonLine className="h-4 w-2/3 mt-6" />
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-black/90 font-serif italic text-lg leading-relaxed">
                                            "{settings?.contact_quote || "Music is the language of the spirit. Stay tuned for more soulful words."}"
                                        </p>
                                        <p className="mt-4 text-sm font-bold text-black tracking-widest">— TEMIDAYO</p>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="bg-white p-8 sm:p-10 border border-black/10 shadow-[0_10px_40px_rgba(0,0,0,0.08)]">
                            <h3 className="text-2xl font-medium text-black mb-8" style={{ fontFamily: "Playfair Display, serif" }}>
                                Send a Message
                            </h3>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold tracking-widest text-black" htmlFor="name">NAME</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full bg-[#fafafa] border-b-2 border-black/10 px-4 py-3 focus:outline-none focus:border-jazz-brown transition-colors text-black"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold tracking-widest text-black" htmlFor="email">EMAIL</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full bg-[#fafafa] border-b-2 border-black/10 px-4 py-3 focus:outline-none focus:border-jazz-brown transition-colors text-black"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold tracking-widest text-black" htmlFor="subject">SUBJECT</label>
                                    <select
                                        id="subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        className="w-full bg-[#fafafa] border-b-2 border-black/10 px-4 py-3 focus:outline-none focus:border-jazz-brown transition-colors text-black appearance-none rounded-none"
                                    >
                                        <option value="">Select a topic...</option>
                                        <option value="Booking">Booking Inquiry</option>
                                        <option value="Press">Press / Interview</option>
                                        <option value="Collaboration">Collaboration</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-bold tracking-widest text-black" htmlFor="message">MESSAGE</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows={6}
                                        className="w-full bg-[#fafafa] border-b-2 border-black/10 px-4 py-3 focus:outline-none focus:border-jazz-brown transition-colors text-black resize-none"
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full bg-black text-white py-4 flex items-center justify-center gap-2 hover:bg-jazz-brown transition-colors duration-300 group mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "1.5px" }}
                                >
                                    {submitting ? (
                                        <>
                                            SENDING...
                                            <Loader2 size={16} className="animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            SEND MESSAGE
                                            <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                    </div>

                </div>
            </main>

            <JazzFooter />
        </div>
    );
}
